import { Fragment, useEffect, ReactNode } from 'react'
import { useRouter } from 'next/router'

import dynamic from 'next/dynamic'

import SplashScreen from '~/components/SplashScreen'
import useCurrentUser from '~/hooks/useCurrentUser'
import authService from '~/services/auth.service'
import ROUTES from '~/shared/constants/routes.const'

const Portal = dynamic(() => import('~/components/Portal'), { ssr: false })

interface AuthGuardProps {
  children: ReactNode
  role?: string | string[]
  showSplash?: boolean
  redirect?: string
  hideOnly?: boolean
  redirectQuery?: Record<string, string>
}

function AuthGuard({
  children,
  role,
  showSplash,
  redirect,
  hideOnly,
  redirectQuery,
}: AuthGuardProps): React.ReactElement | null {
  const authenticated = authService.isAuthenticated()
  const user = useCurrentUser()
  const router = useRouter()
  const authorizedByRole = role ? (Array.isArray(role) ? role.includes(user?.role || '') : role === user?.role) : false
  const isCampaignsPage = router.pathname.startsWith(ROUTES.CAMPAIGNS)

  useEffect(
    function redirectUnAuthorized() {
      if (router.isReady && !authenticated && !hideOnly && !isCampaignsPage) {
        const toPath = redirect ?? ROUTES.LOGIN
        router.push({
          pathname: toPath,
          query: redirectQuery,
        })
      }
    },
    [router, router.isReady, redirect, authenticated, hideOnly, redirectQuery, isCampaignsPage],
  )

  useEffect(
    function redirectIfWrongRole() {
      const roleIsArray = Array.isArray(role)
      if (user && role && !hideOnly) {
        if (user.hasPermissions) return

        if (roleIsArray && role.indexOf(user.role) === -1) {
          router.push(ROUTES.REGISTER)
        } else if (!roleIsArray && role !== user.role) {
          router.push(ROUTES.REGISTER)
        }
      }
    },
    [role, user, router, hideOnly],
  )

  if ((!user && hideOnly) || (!authorizedByRole && hideOnly)) {
    return null
  }

  if (!user && !isCampaignsPage && showSplash) {
    return (
      <Portal targetDomId="__next">
        <SplashScreen />
      </Portal>
    )
  }

  return <Fragment>{children}</Fragment>
}

export default AuthGuard
